









































import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import ContentMixin from "@/mixins/ContentMixin";
import { Config } from "@/plugins/config";
import {
  DynamicAudioContent,
  DynamicAudioPiece,
} from "@/store/modules/content/types";

@Component({
  name: "dynamic-audio-player",
})
export default class DynamicAudioPlayer extends mixins(ContentMixin) {
  @Prop({ default: null })
  public contentDa!: any;

  @Prop({ default: false })
  public draggable!: boolean;

  public audio = "";
  public html!: string;
  public pieces!: DynamicAudioPiece[];

  public alteredHtml = "";
  public alteredPieces: DynamicAudioPiece[] = [];

  private isPlayingInterval = 0;
  private currentTime = 0;

  public created() {
    this.initMarks();
  }

  public initMarks() {
    const cda = this.contentDa as DynamicAudioContent;
    if (cda && !cda.hasOwnProperty("pieces")) {
      this.alteredHtml = cda.text;
      return;
    }

    this.alteredPieces = [];
    this.pieces = cda.pieces;
    this.html = cda.text;

    if (cda.audio.length) {
      this.audio = Config.Files + cda.audio;
    }

    if (this.pieces) {
      for (const piece of this.pieces) {
        const nPiece = { ...piece };
        nPiece.marked = false;
        this.alteredPieces.push(nPiece);
      }

      this.alteredHtml = this.spaceRemarker(this.tagRemarker(this.html), false);
    } else {
      this.alteredHtml = this.spaceRemarker(this.tagRemarker(this.html), true);
    }
  }

  /**
   * Remarks basic tags on a content
   * @param {string} content
   * @return {string}
   */
  public tagRemarker(content: string): string {
    const tags = ["strong", "b", "i", "u", "s", "sub", "sup"];

    for (const tag of tags) {
      // "tu" is added to the regex to avoid s to get confused with strong, sub and sup and to supress tcustom
      const regexp = new RegExp(
        "<\\s*" + tag + "[^tu>]*>(.*?)<\\s*\\/\\s*" + tag + ">",
        "gmi"
      );

      const matches = content.match(regexp);
      if (matches) {
        for (const match of matches) {
          const words = match.trim().split(" ");
          if (words) {
            let phrase = " " + words.shift();

            for (const word of words) {
              phrase += "</" + tag + "> <" + tag + ">" + word;
            }

            if (phrase) {
              content = content.replace(match, phrase);
            }
          }
        }
      }
    }

    return content;
  }

  /**
   * Remakes the words adding spans based on the space between them
   * @param content
   */
  public spaceRemarker(content: string, push = true): string {
    // Can't have two tags together
    content = content.replace(/></gm, "> <");

    const words = content.split(" ");
    let ret = "";

    for (const x in words) {
      let word = words[x];

      if (word.trim().length === 0) {
        continue;
      }

      const matchOpenTags = word.match(/<[a-zA-Z][^>]*>/gi);
      const matchCloseTags = word.match(/<\/[a-zA-Z][^>]*>/gi);

      if (matchOpenTags && matchOpenTags.length) {
        const last = matchOpenTags.pop();
        word = word.replace(
          last as string,
          last + "<span class='word-" + x + "'>"
        );
      } else {
        word = "<span class='word-" + x + "'>" + word;
      }

      if (matchCloseTags && matchCloseTags.length) {
        const first = matchCloseTags.shift();
        word = word.replace(first as string, "</span>" + first);
      } else {
        word += "</span>";
      }

      const clean = word
        .replace(/<([A-Z][A-Z0-9]*)\b[^>]*>/gi, "")
        .replace(/<\/([A-Z][A-Z0-9]*)\b[^>]*>/gi, "");

      if (clean.trim().length > 0 && push) {
        this.alteredPieces.push({
          piece: word,
          marked_piece: word.replace(
            "class='word",
            "class='bg-secondary text-white word"
          ),
          clean,
          time: 0,
          marked: false,
        });
      }

      ret += word + " ";
    }

    return ret;
  }

  /**
   * Start the interval when the play button is hit
   * @param media
   */
  public onPlay(media: Event): void {
    this.isPlayingInterval = setInterval(() => {
      this.onSeek(media);
    }, 1);
  }

  /**
   * Kills the interval when the audio stops playing
   */
  public onPause(): void {
    clearInterval(this.isPlayingInterval);
    this.isPlayingInterval = 0;
  }

  /**
   * Kills the interval when the audio stops playing
   */
  public onEnd(): void {
    const cda = this.contentDa as DynamicAudioContent;
    if (cda && !cda.hasOwnProperty("pieces")) {
      this.alteredHtml = cda.text;
      return;
    }

    this.pieces = cda.pieces;
    this.html = cda.text;

    this.alteredHtml = this.spaceRemarker(this.tagRemarker(this.html), false);
  }

  /**
   * Actions executed when the audio seek bar is moving
   * @param media
   */
  public onSeek(media: Event): void {
    // Correctly formatted with 2 decimals audio time
    this.currentTime = parseFloat(
      Math.round(parseFloat((media.target as any).currentTime + "e+2")) + "e-2"
    );

    // Unmarks all future pieces
    this.unmarkFuturePieces();

    // Removes the <mark> tag based on future ones
    this.unWriteHtmlMarkOnTime();

    // Writes the <mark> tag based on current time
    this.writeHtmlMarkOnTime();
  }

  /**
   * Sets as not marked all pieces in the future of current time
   */
  public unmarkFuturePieces(): void {
    for (let x = 0; x < this.alteredPieces.length; x++) {
      if (this.alteredPieces[x].time > this.currentTime) {
        this.alteredPieces[x].marked = false;
      }
    }
  }

  /**
   * Unwrites the <mark> for future pieces
   */
  public unWriteHtmlMarkOnTime(): void {
    for (const piece of this.alteredPieces) {
      if (piece.time > this.currentTime && !piece.marked) {
        this.alteredHtml = this.alteredHtml.replace(
          piece.marked_piece,
          piece.piece
        );
        piece.marked = false;
      }
    }
  }

  /**
   * Writes the effective html on the current mark
   */
  public writeHtmlMarkOnTime(): void {
    for (const piece of this.alteredPieces) {
      if (piece.time > 0 && piece.time <= this.currentTime && !piece.marked) {
        this.alteredHtml = this.alteredHtml.replace(
          piece.piece,
          piece.marked_piece
        );
        piece.marked = true;
      }
    }
  }

  //Audio control
  public play() {
    const audio: any = this.$refs.audioPlayer;
    if (audio.paused) {
      audio.play();
      this.isPlayingInterval = 1;
    } else {
      audio.pause();
      this.isPlayingInterval = 0;
    }
  }
}
